import { create } from 'zustand';

import examService, { Exam, CreateExamData, UpdateExamData, ExamListParams } from '../services/exam.service';

interface ExamState {
    exams: Exam[];
    currentExam: Exam | null;
    total: number;
    loading: boolean;
    error: string | null;

    // Actions
    fetchExams: (params?: ExamListParams) => Promise<void>;
    fetchExam: (id: string) => Promise<void>;
    createExam: (data: CreateExamData) => Promise<Exam>;
    updateExam: (id: string, data: UpdateExamData) => Promise<Exam>;
    deleteExam: (id: string) => Promise<void>;
    publishExam: (id: string) => Promise<void>;
    archiveExam: (id: string) => Promise<void>;
    unarchiveExam: (id: string) => Promise<void>;
    setCurrentExam: (exam: Exam | null) => void;
    clearError: () => void;
}

export const useExamStore = create<ExamState>((set) => ({
    exams: [],
    currentExam: null,
    total: 0,
    loading: false,
    error: null,

    fetchExams: async (params) => {
        set({ loading: true, error: null });
        try {
            const { exams, total } = await examService.listExams(params);
            set({ exams, total, loading: false });
        } catch (error: any) {
            set({ error: error.message, loading: false });
        }
    },

    fetchExam: async (id) => {
        set({ loading: true, error: null });
        try {
            const exam = await examService.getExam(id);
            set({ currentExam: exam, loading: false });
        } catch (error: any) {
            set({ error: error.message, loading: false });
        }
    },

    createExam: async (data) => {
        set({ loading: true, error: null });
        try {
            const exam = await examService.createExam(data);
            set((state) => ({
                exams: [exam, ...state.exams],
                currentExam: exam,
                total: state.total + 1,
                loading: false,
            }));
            return exam;
        } catch (error: any) {
            set({ error: error.message, loading: false });
            throw error;
        }
    },

    updateExam: async (id, data) => {
        set({ loading: true, error: null });
        try {
            const exam = await examService.updateExam(id, data);
            set((state) => ({
                exams: state.exams.map((e) => (e._id === id ? exam : e)),
                currentExam: state.currentExam?._id === id ? exam : state.currentExam,
                loading: false,
            }));
            return exam;
        } catch (error: any) {
            set({ error: error.message, loading: false });
            throw error;
        }
    },

    deleteExam: async (id) => {
        set({ loading: true, error: null });
        try {
            await examService.deleteExam(id);
            set((state) => ({
                exams: state.exams.filter((e) => e._id !== id),
                currentExam: state.currentExam?._id === id ? null : state.currentExam,
                total: state.total - 1,
                loading: false,
            }));
        } catch (error: any) {
            set({ error: error.message, loading: false });
            throw error;
        }
    },

    publishExam: async (id) => {
        set({ loading: true, error: null });
        try {
            const exam = await examService.publishExam(id);
            set((state) => ({
                exams: state.exams.map((e) => (e._id === id ? exam : e)),
                currentExam: state.currentExam?._id === id ? exam : state.currentExam,
                loading: false,
            }));
        } catch (error: any) {
            set({ error: error.message, loading: false });
            throw error;
        }
    },

    archiveExam: async (id) => {
        set({ loading: true, error: null });
        try {
            const exam = await examService.archiveExam(id);
            set((state) => ({
                exams: state.exams.map((e) => (e._id === id ? exam : e)),
                currentExam: state.currentExam?._id === id ? exam : state.currentExam,
                loading: false,
            }));
        } catch (error: any) {
            set({ error: error.message, loading: false });
            throw error;
        }
    },

    unarchiveExam: async (id) => {
        set({ loading: true, error: null });
        try {
            const exam = await examService.unarchiveExam(id);
            set((state) => ({
                exams: state.exams.map((e) => (e._id === id ? exam : e)),
                currentExam: state.currentExam?._id === id ? exam : state.currentExam,
                loading: false,
            }));
        } catch (error: any) {
            set({ error: error.message, loading: false });
            throw error;
        }
    },

    setCurrentExam: (exam) => {
        set({ currentExam: exam });
    },

    clearError: () => {
        set({ error: null });
    },
}));
